/**
 * useFileUpload — orchestrates the full upload-to-result lifecycle.
 *
 * Handles routing:
 * - Files ≤ SYNC_SIZE_LIMIT_MB → POST /upload → ConversionResult inline
 * - Files > SYNC_SIZE_LIMIT_MB → POST /jobs  → job_id, then delegates to useJobPoller
 *
 * The caller only needs to call `upload(file)` and observe the store state.
 */

import { useCallback } from "react";
import { ApiError } from "../api/client";
import { convertSync, enqueueJob, getPresignedUploadUrl, uploadToS3 } from "../api/endpoints";
import { useConversionStore } from "../store/conversionStore";

// Must match the backend SYNC_MAX_SIZE_MB setting
const SYNC_SIZE_LIMIT_BYTES = 5 * 1024 * 1024;

/** Returns the upload function and a reset function. */
export function useFileUpload() {
  const store = useConversionStore();

  const upload = useCallback(
    async (file: File) => {
      store.setUploading(0);

      try {
        if (file.size <= SYNC_SIZE_LIMIT_BYTES) {
          // Synchronous path: result comes back in the response
          const result = await convertSync(file, store.options, (pct) => {
            store.setUploadProgress(pct);
          });
          store.setDone(result);
        } else {
          // Async path: upload directly to S3 (bypasses Lambda's 6 MB limit),
          // then enqueue the conversion job.
          const { job_id, upload_url } = await getPresignedUploadUrl();

          await uploadToS3(upload_url, file, (pct) => {
            store.setUploadProgress(pct);
          });

          const job = await enqueueJob(job_id, file.name, store.options);
          store.setProcessing(job.job_id);
          // useJobPoller (mounted in the component tree) takes over from here
        }
      } catch (err) {
        if (err instanceof ApiError) {
          store.setError({ error_code: err.error_code, message: err.message });
        } else {
          store.setError({
            error_code: "UNKNOWN_ERROR",
            message: err instanceof Error ? err.message : "An unexpected error occurred.",
          });
        }
      }
    },
    [store]
  );

  return { upload, reset: store.reset };
}
